import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import WorkCard from "@/components/feature/WorkCard";
import Breadcrumb from "@/components/feature/Breadcrumb";
import { useSeo } from "@/hooks/useSeo";
import { animeList } from "@/mocks/anime";
import { categories, siteInfo } from "@/mocks/site";
import type { AnimeWork } from "@/types";

export default function Works() {
  const [searchParams, setSearchParams] = useSearchParams();
  const categoryParam = searchParams.get("category") || "all";
  const tagParam = searchParams.get("tag") || "";
  const qParam = searchParams.get("q") || "";
  const [keyword, setKeyword] = useState(qParam);

  useEffect(() => {
    setKeyword(qParam);
  }, [qParam]);

  const allTags = useMemo(() => {
    const set = new Set<string>();
    animeList.forEach((item) => item.tags.forEach((tag) => set.add(tag)));
    return Array.from(set);
  }, []);

  const filtered = useMemo(() => {
    const kw = qParam.trim().toLowerCase();
    return animeList.filter((item) => {
      const matchCategory = categoryParam === "all" || item.category === categoryParam;
      const matchTag = !tagParam || item.tags.includes(tagParam);
      const matchKeyword =
        !kw ||
        item.title.toLowerCase().includes(kw) ||
        item.titleJp.toLowerCase().includes(kw) ||
        item.studio.toLowerCase().includes(kw) ||
        item.tags.some((tag) => tag.toLowerCase().includes(kw)) ||
        item.summary.toLowerCase().includes(kw);
      return matchCategory && matchTag && matchKeyword;
    }) as AnimeWork[];
  }, [categoryParam, tagParam, qParam]);

  const activeCategory = categories.find((cat) => cat.id === categoryParam);

  const updateParam = (key: string, value: string) => {
    const next = new URLSearchParams(searchParams);
    if (value) {
      next.set(key, value);
    } else {
      next.delete(key);
    }
    setSearchParams(next, { replace: true });
  };

  useSeo({
    title: "作品库 - 动漫作品资料与推荐 | 樱花动漫社",
    description:
      "樱花动漫社作品库收录经编辑整理的动画作品资料，支持按热血冒险、青春治愈、科幻机战、恋爱日常、悬疑奇幻分类筛选，并可通过标签与关键词快速查找。",
    keywords: "动漫作品库,动漫分类,动漫筛选,动画推荐,动漫资料",
    path: "/works",
    jsonLd: [
      {
        "@context": "https://schema.org",
        "@type": "CollectionPage",
        name: "作品库",
        url: `${siteInfo.domain}/works`,
        description: "按分类、标签与关键词整理的动画作品资料集合。",
        inLanguage: "zh-CN",
        isPartOf: { "@type": "WebSite", name: siteInfo.name, url: siteInfo.domain },
      },
      {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        itemListElement: [
          { "@type": "ListItem", position: 1, name: "首页", item: siteInfo.domain },
          { "@type": "ListItem", position: 2, name: "作品库", item: `${siteInfo.domain}/works` },
        ],
      },
      {
        "@context": "https://schema.org",
        "@type": "ItemList",
        name: "作品库列表",
        numberOfItems: filtered.length,
        itemListElement: filtered.map((item, index) => ({
          "@type": "ListItem",
          position: index + 1,
          name: item.title,
          url: `${siteInfo.domain}/works/${item.id}`,
        })),
      },
    ],
  });

  return (
    <div className="mx-auto max-w-7xl px-4 py-10 md:px-6 md:py-14">
      <Breadcrumb items={[{ label: "作品库" }]} />

      <div className="mt-6 max-w-3xl">
        <h1 className="font-heading text-3xl font-700 tracking-tight text-foreground-950 md:text-4xl">
          作品库
        </h1>
        <p className="mt-4 text-sm leading-relaxed text-foreground-600 md:text-base">
          这里按分类与题材整理每一部作品的资料。可以先在左侧挑一个分类，再用标签或关键词缩小范围，
          点击卡片即可进入详情页查看制作团队、剧情简介与观影提示。
        </p>
      </div>

      <div className="mt-10 grid grid-cols-1 gap-8 lg:grid-cols-[260px_1fr]">
        {/* 侧栏筛选 */}
        <aside className="lg:sticky lg:top-24 lg:self-start">
          <div className="rounded-xl border border-background-200 bg-background-50 p-5">
            <h2 className="font-heading text-base font-600 text-foreground-950">按分类浏览</h2>
            <ul className="mt-4 space-y-1">
              <li>
                <button
                  type="button"
                  onClick={() => updateParam("category", "")}
                  className={`flex w-full items-center gap-2 rounded-md px-3 py-2 text-left text-sm transition-colors ${
                    categoryParam === "all"
                      ? "bg-primary-100 text-primary-800"
                      : "text-foreground-700 hover:bg-background-100"
                  }`}
                >
                  <i className="ri-apps-2-line" aria-hidden="true"></i>
                  全部作品
                  <span className="ml-auto text-xs text-foreground-500">{animeList.length}</span>
                </button>
              </li>
              {categories.map((cat) => {
                const count = animeList.filter((item) => item.category === cat.id).length;
                return (
                  <li key={cat.id}>
                    <button
                      type="button"
                      onClick={() => updateParam("category", cat.id)}
                      className={`flex w-full items-center gap-2 rounded-md px-3 py-2 text-left text-sm transition-colors ${
                        categoryParam === cat.id
                          ? "bg-primary-100 text-primary-800"
                          : "text-foreground-700 hover:bg-background-100"
                      }`}
                    >
                      <i className={cat.icon} aria-hidden="true"></i>
                      {cat.name}
                      <span className="ml-auto text-xs text-foreground-500">{count}</span>
                    </button>
                  </li>
                );
              })}
            </ul>
          </div>

          <div className="mt-6 rounded-xl border border-background-200 bg-background-50 p-5">
            <h2 className="font-heading text-base font-600 text-foreground-950">题材标签</h2>
            <div className="mt-4 flex flex-wrap gap-2">
              {allTags.map((tag) => (
                <button
                  key={tag}
                  type="button"
                  onClick={() => updateParam("tag", tagParam === tag ? "" : tag)}
                  className={`rounded-full px-3 py-1 text-xs transition-colors ${
                    tagParam === tag
                      ? "bg-secondary-500 text-background-50"
                      : "bg-secondary-100 text-secondary-900 hover:bg-secondary-200"
                  }`}
                >
                  {tag}
                </button>
              ))}
            </div>
          </div>
        </aside>

        {/* 内容区 */}
        <section>
          <div className="flex flex-col gap-3 rounded-xl border border-background-200 bg-background-50 p-4 sm:flex-row sm:items-center">
            <form
              className="flex flex-1 items-center gap-2 rounded-md border border-background-200 bg-background-100 px-3 py-2"
              onSubmit={(event) => {
                event.preventDefault();
                updateParam("q", keyword.trim());
              }}
            >
              <i className="ri-search-line text-foreground-500" aria-hidden="true"></i>
              <input
                type="search"
                value={keyword}
                onChange={(event) => setKeyword(event.target.value)}
                placeholder="搜索作品名、制作组或题材…"
                aria-label="搜索作品"
                className="w-full bg-transparent text-sm text-foreground-900 outline-none placeholder:text-foreground-400"
              />
              <button
                type="submit"
                className="whitespace-nowrap rounded-md bg-primary-500 px-3 py-1.5 text-xs font-500 text-background-50 transition-colors hover:bg-primary-600"
              >
                搜索
              </button>
            </form>
            <p className="whitespace-nowrap text-sm text-foreground-600">
              共 <span className="font-600 text-primary-700">{filtered.length}</span> 部作品
            </p>
          </div>

          {(categoryParam !== "all" || tagParam || qParam) && (
            <div className="mt-4 flex flex-wrap items-center gap-2 text-xs text-foreground-600">
              <span>当前筛选：</span>
              {categoryParam !== "all" && activeCategory && (
                <span className="rounded-full bg-primary-100 px-3 py-1 text-primary-800">
                  {activeCategory.name}
                </span>
              )}
              {tagParam && (
                <span className="rounded-full bg-secondary-100 px-3 py-1 text-secondary-900">
                  #{tagParam}
                </span>
              )}
              {qParam && (
                <span className="rounded-full bg-accent-100 px-3 py-1 text-accent-900">
                  关键词「{qParam}」
                </span>
              )}
              <button
                type="button"
                onClick={() => setSearchParams(new URLSearchParams(), { replace: true })}
                className="ml-1 flex items-center gap-1 text-primary-700 hover:text-primary-800"
              >
                <i className="ri-close-circle-line" aria-hidden="true"></i>
                清除全部
              </button>
            </div>
          )}

          {filtered.length > 0 ? (
            <div className="mt-6 grid grid-cols-2 gap-4 md:gap-6 lg:grid-cols-3">
              {filtered.map((work) => (
                <WorkCard key={work.id} work={work} />
              ))}
            </div>
          ) : (
            <div className="mt-6 flex flex-col items-center rounded-xl border border-dashed border-background-300 bg-background-50 px-6 py-16 text-center">
              <span className="flex h-14 w-14 items-center justify-center rounded-full bg-background-100 text-foreground-400">
                <i className="ri-emotion-sad-line text-2xl" aria-hidden="true"></i>
              </span>
              <p className="mt-4 font-heading text-base font-600 text-foreground-900">没有找到匹配的作品</p>
              <p className="mt-2 text-sm text-foreground-500">试试更换关键词，或者清空筛选条件重新浏览。</p>
              <button
                type="button"
                onClick={() => setSearchParams(new URLSearchParams(), { replace: true })}
                className="mt-5 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2 text-sm font-500 text-background-50 transition-colors hover:bg-primary-600"
              >
                查看全部作品
              </button>
            </div>
          )}

          <div className="mt-10 rounded-xl bg-secondary-50 p-6">
            <h2 className="font-heading text-base font-600 text-foreground-950">还在犹豫？看看编辑怎么选</h2>
            <p className="mt-2 text-sm leading-relaxed text-foreground-600">
              如果你希望直接获得推荐，可以前往
              <Link to="/ranking" className="mx-1 font-500 text-primary-700 hover:text-primary-800">
                编辑精选榜
              </Link>
              ，按心情方向找到合适的作品。也可以阅读
              <Link to="/articles" className="mx-1 font-500 text-primary-700 hover:text-primary-800">
                资讯专栏
              </Link>
              了解每季新番动态。
            </p>
          </div>
        </section>
      </div>
    </div>
  );
}